import React, { useState, useEffect } from 'react';
import { removeDangereous } from '../../utils/removeDangereous';
import { useSvgCache } from './SvgCacheContext';

export interface IconSVGProps {
  path: string;
  size?: number | string;
  className?: string;
}

const IconSVG: React.FC<IconSVGProps> = ({
  path,
  size,
  className = ''
}) => {
  const { getCached, loadSvg } = useSvgCache();
  const [svgContent, setSvgContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(false);
        // Try cache first
        const cached = getCached(path);
        if (cached?.content) {
          if (!isMounted) return;
          setSvgContent(cached.content);
          setLoading(false);
          return;
        }
        // Trigger load (deduped by context)
        const text = await loadSvg(path);
        if (!isMounted) return;
        setSvgContent(text);
      } catch (err) {
        if (!isMounted) return;
        setError(true);
        console.error('Error loading SVG:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    run();
    return () => { isMounted = false; };
  }, [path, getCached, loadSvg]);

  const sizeValue = typeof size === 'number' ? size : undefined;

  if (loading) {
    return (
      <div
        className={`icon-svg icon-svg-loading ${className}`}
        style={{ width: sizeValue, height: sizeValue }}
      />
    );
  }

  if (error || !svgContent) {
    return (
      <div
        className={`icon-svg icon-svg-error ${className}`}
        style={{ width: sizeValue, height: sizeValue }}
      />
    );
  }

  // Modify SVG content to apply size
  const modifiedSvgContent = svgContent
    .replace(/<svg([^>]*)>/i, (match, attributes) => {
      let attrs: string = attributes;
      // Set width and height
      if (sizeValue) {
        attrs = attrs.replace(/width="[^"]*"/i, `width="${sizeValue}"`);
        attrs = attrs.replace(/height="[^"]*"/i, `height="${sizeValue}"`);

        // If width/height don't exist, add them
        if (!attrs.includes('width=')) {
          attrs += ` width="${sizeValue}"`;
        }
        if (!attrs.includes('height=')) {
          attrs += ` height="${sizeValue}"`;
        }
      }


      return `<svg${attrs}>`;
    });

  return (
    <div
      className={`icon-svg ${className}`}
      style={{
        display: 'inline-block',
        lineHeight: 0
      }}
      dangerouslySetInnerHTML={{ __html: removeDangereous(modifiedSvgContent) }}
    />
  );
};

export default IconSVG;
